/* =========================================================================
   Glitter Gift Baskets - Cart Redesign JS

   Scope (after the gift-note decision):
     - Quantity stepper + remove that change the cart and then do a FULL page
       reload (preserving ?view=redesign). This is the safest path for the
       non-negotiable: Giftship re-initializes from the persisted cart state, so
       the global gift note (To / Message / From) survives add / remove / qty
       changes. We never partial-render the cart, so there is no window in which
       Giftship's save could be interrupted.

   The gift note itself is Giftship's own GLOBAL gift-message field (one per
   order). It captures, saves, and counts characters on its own. We do NOT mirror
   it into a theme preview here. The styled card preview is a separate
   personalization-modal project. Multiship handles per-address messages.

   HARD RULES (Giftship skill + CLAUDE.md): this file is READ-ONLY against
   Giftship's DOM. No MutationObserver, no rewriting Giftship labels/inputs.
   ========================================================================= */
(function () {
  'use strict';

  // Take scroll restoration off the browser for our own cart reloads. After a
  // qty/remove or upsell reload, Giftship re-injects #gsAppContainer late, so the
  // browser's auto scroll-restore first lands on the short pre-Giftship page
  // (footer visible), then jumps again once Giftship paints. Manual restoration
  // makes every reload land at the top of the cart cleanly. This only sets a
  // window scroll behaviour; it never touches Giftship DOM or inputs.
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  // Change a cart line, then full-reload. Full reload (not a partial re-render)
  // is deliberate: Giftship re-initializes cleanly from the saved cart, so the
  // gift note is never dropped, and the ?view=redesign URL is preserved.
  function changeLine(line, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart change failed: ' + res.status);
      window.location.reload();
    }).catch(function (err) {
      console.error('[cart-redesign] ' + err.message);
    });
  }

  function onClick(e) {
    // Bring the Giftship gift note into view when the customer hits checkout. If
    // the required gift note is empty, Giftship blocks the submit and shows its own
    // error; this just makes sure the field is on screen for her. On a valid cart
    // the form submits and the page navigates, so the scroll is not seen. READ-ONLY
    // against Giftship: we scroll the window to a documented Giftship container
    // (#gsAppContainer); we never mutate its DOM or inputs, never preventDefault
    // here, and use no MutationObserver.
    var checkoutBtn = e.target.closest ? e.target.closest('button[name="checkout"]') : null;
    if (checkoutBtn) {
      var gsBox = document.querySelector('#gsAppContainer');
      if (gsBox) gsBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    var stepBtn = e.target.closest ? e.target.closest('[data-step]') : null;
    if (stepBtn && !stepBtn.disabled) {
      var card = stepBtn.closest('.cr-item');
      if (!card) return;
      var line = parseInt(card.getAttribute('data-line'), 10);
      var qtyEl = card.querySelector('[data-qty]');
      var cur = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
      var dir = parseInt(stepBtn.getAttribute('data-step'), 10) || 0;
      var next = cur + dir;
      if (next < 1) return; // remove is a separate control
      e.preventDefault();
      changeLine(line, next);
      return;
    }

    var removeBtn = e.target.closest ? e.target.closest('[data-cr-remove]') : null;
    if (removeBtn) {
      var c = removeBtn.closest('.cr-item');
      if (!c) return;
      var ln = parseInt(c.getAttribute('data-line'), 10);
      e.preventDefault();
      changeLine(ln, 0);
    }
  }

  // ---- Route Giftship upsell add/remove through a clean full reload ----------
  // Giftship empties and re-paints #gsAppContainer on its own upsell add/remove,
  // which jumps the checkout below it (reserve-height CSS could not hold it). So
  // instead of fighting the ajax re-paint, we reload after Giftship finishes,
  // exactly like the qty/remove path above: the cart is already updated server-
  // side, the page comes back fully rendered with no shift, and the saved gift
  // note survives. READ-ONLY against Giftship: a passive click flag plus the
  // documented giftship.reloaded event. We never touch Giftship's DOM or inputs.
  var pendingUpsell = false;
  function flagUpsell(e) {
    var t = e.target;
    if (t && t.closest && t.closest('.gs__upsell-btn, .gs__multi-carousel-upsell-btn')) {
      pendingUpsell = true;
    }
  }
  function onGiftshipReloaded() {
    if (pendingUpsell) {
      pendingUpsell = false;
      window.location.reload();
    }
  }

  // ---- Sticky checkout bar (emulates the Mother's Day product sticky ATC) ----
  // A slim bottom bar (rendered OUTSIDE the form, so Giftship never injects into
  // it) slides up when the real .cr-btn-checkout scrolls out of view, after the
  // shopper has scrolled at least once, and slides away when it returns. The bar
  // button is type=button and forwards a click to the real .cr-btn-checkout, so
  // the gift-note scroll + normal checkout submit fire exactly the same. Mirrors
  // initStickyAtc on the Mother's Day product page. READ-ONLY against Giftship:
  // we only observe our own checkout button and never touch Giftship's DOM.
  function initStickyCta(root) {
    var bar = root.querySelector('[data-cr-sticky-cta]');
    var mainBtn = root.querySelector('.cr-btn-checkout');
    if (!bar || !mainBtn) return;
    if (!('IntersectionObserver' in window)) return;

    var proxyBtn = bar.querySelector('[data-cr-sticky-checkout]');
    if (proxyBtn) {
      proxyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        mainBtn.click();
      });
    }

    // The real checkout button starts BELOW the fold on the cart (Giftship pushes
    // it down), so an IntersectionObserver alone never fires while scrolling down
    // from the top. Track the button's on/off-screen state, gate on the first
    // scroll, and re-evaluate on both the observer fire AND that first scroll, so
    // the bar appears as soon as the shopper scrolls with the button off-screen.
    var hasEngaged = false;
    var btnOffscreen = false;
    function apply() {
      var shouldShow = hasEngaged && btnOffscreen;
      bar.classList.toggle('is-visible', shouldShow);
      bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      document.body.classList.toggle('cr-sticky-cta-visible', shouldShow);
    }
    window.addEventListener('scroll', function () { hasEngaged = true; apply(); }, { once: true, passive: true });

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { btnOffscreen = !entry.isIntersecting; });
      apply();
    }, { rootMargin: '0px', threshold: 0 });

    // Let layout settle (Giftship paints #gsAppContainer late) before first observe.
    setTimeout(function () { observer.observe(mainBtn); }, 800);
  }

  function init() {
    var root = document.querySelector('.cr-cart');
    if (!root) return;
    root.addEventListener('click', onClick);
    initStickyCta(root);

    // Reload after a Giftship upsell add/remove (documented giftship.reloaded),
    // scoped by a passive click flag so other re-inits never reload.
    document.addEventListener('click', flagUpsell, true);
    document.addEventListener('giftship.reloaded', onGiftshipReloaded);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
